/**
 * LocationPicker: which of a vendor's locations this storefront orders from,
 * and the dialog to pick another (4 Oct 2026).
 * ─────────────────────────────────────────────────────────────────────────────
 * The Brands and Restaurants pages show ONE card per vendor, opened on the
 * location that serves the shopper best (utils/vendorOutlets.onePerVendor).
 * This bar is where the shopper sees that choice and changes it: "Ordering
 * from Yalelo Kawempe, 1.2 km from Bulenga, Wakiso   [Change branch]", and a
 * dialog listing every branch, best first, then nearest first, each with its
 * open state and whether it delivers to them.
 *
 * The action says what it changes: a bare "Change" did not read as "order
 * from another branch" (Izi, 4 Oct 2026), and the dialog has a second Change,
 * for the delivery location.
 *
 * Both name the point the distances are measured from (utils/deliveryPoint),
 * and the dialog offers the tap to change it, which opens the site's own
 * LocationPrompt. A distance that does not say where from reads as "from
 * where I am", which a location set hours ago may not be (4 Oct 2026).
 *
 * Renders nothing for a vendor with a single location, so a one-shop brand or
 * kitchen looks exactly as it did.
 *
 * The page decides what choosing means (every storefront moves the cart
 * along and opens that branch); this component only reports the pick through
 * `onChoose`. When the page says a pick needs a yes first (`confirmChoice`,
 * for a cart bound to another branch) the picker asks "Move your cart to
 * <name>?" and reports the pick only on Move cart. A branch can have its own
 * sold out, on/off and price (4 Oct 2026), so the question no longer
 * promises the same prices: the cart is checked there and anything that
 * changed is shown before the shopper pays.
 *
 * Same behaviour and copy as the mobile apps' LocationBar
 * (Lusuku_Expo_Clean app/components/vendor/LocationBar.js).
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LocationPrompt from "./LocationPrompt";
import { deliveryPointPhrase } from "../utils/deliveryPoint";
import { moveCartCopy } from "../utils/branchAvailability";
import {
  locationReaches,
  sortLocationsForPicker,
} from "../utils/vendorOutlets";
import {
  OPEN_BADGE_TONES,
  openNowOf,
  vendorOpenBadge,
} from "../utils/vendorOpenState";

const INK = "#1F2937";
const MUTED = "#6B7280";
const HAIRLINE = "#E5E7EB";

/** What a location is called in the bar, the dialog and the confirm. */
export const locationName = (location, vendorName = "") => {
  if (!location) return "";
  if (location.isOutlet) return location.name || "";
  const name = vendorName || location.name || "";
  return name ? `${name} (main branch)` : "Main branch";
};

/**
 * The name the branch words ("Sold out today at ...") use for the location
 * on screen: the vendor's own name for a vendor with one location, exactly
 * as before branches, else the branch's name or "<vendor> (main branch)",
 * as the apps' storefronts name it. `outlet` is the open branch, or null for
 * the main location.
 */
export const hereNameOf = (locations, outlet, vendorName = "") =>
  (Array.isArray(locations) ? locations.length : 0) < 2
    ? vendorName
    : locationName(
        outlet ? { isOutlet: true, name: outlet.name || "" } : { isOutlet: false },
        vendorName
      );

const distanceText = (location) =>
  location?.distance ? `${location.distance} km` : null;

function LocationRow({ location, vendorName, selected, accent, onChoose }) {
  const badge = vendorOpenBadge(location);
  const reaches = locationReaches(location);
  const km = distanceText(location);
  const name = locationName(location, vendorName);
  return (
    <li>
      <Row
        type="button"
        $selected={selected}
        onClick={() => onChoose(location)}
        aria-current={selected ? "true" : undefined}
        aria-label={`${name}${km ? `, ${km}` : ""}`}
      >
        <RowIcon aria-hidden="true">
          {selected ? (
            <CheckCircleIcon style={{ fontSize: 22, color: accent }} />
          ) : (
            <StorefrontOutlinedIcon style={{ fontSize: 22, color: MUTED }} />
          )}
        </RowIcon>
        <RowCopy>
          <RowName>{name}</RowName>
          {location.address ? <RowAddress>{location.address}</RowAddress> : null}
          <RowTags>
            {badge ? (
              <Tag style={{ background: OPEN_BADGE_TONES[badge.tone].solid, color: "#FFFFFF" }}>
                {badge.label}
              </Tag>
            ) : null}
            {reaches === true ? (
              <Tag style={{ background: "#ECFDF5", color: "#047857" }}>Delivers to you</Tag>
            ) : reaches === false ? (
              <Tag style={{ background: "#F3F4F6", color: MUTED }}>Outside delivery area</Tag>
            ) : null}
          </RowTags>
        </RowCopy>
        {km ? <RowKm>{km}</RowKm> : null}
      </Row>
    </li>
  );
}

/**
 * @param {object}   props
 * @param {object[]} props.locations       utils/vendorOutlets.vendorLocationsOf(vendor, ...)
 * @param {string}   props.currentId       the location ordered from now: an outlet id,
 *                                         or the vendor's own id for its main branch
 * @param {string}   [props.vendorName]
 * @param {Function} props.onChoose        (location) => void, only for a DIFFERENT location
 * @param {Function} [props.confirmChoice] (location) => boolean; true asks first
 * @param {object}   [props.deliveryPoint] hooks/useDeliveryPoint, the point the
 *                                         distances are measured from
 * @param {string}   [props.accent]        the storefront's own colour
 * @param {string}   [props.onAccent]      text drawn on the accent (the Switch button)
 * @param {string}   [props.maxWidth]      the bar's widest, centred (the page's content width)
 */
const LocationPicker = ({
  locations,
  currentId,
  vendorName = "",
  onChoose,
  confirmChoice,
  deliveryPoint,
  accent = "#2563EB",
  onAccent = "#FFFFFF",
  maxWidth,
}) => {
  const [open, setOpen] = useState(false);
  // The site's location dialog, opened from "Distances from ... Change".
  const [promptOpen, setPromptOpen] = useState(false);
  const from = deliveryPointPhrase(deliveryPoint);
  // The location waiting on "Move your cart to <name>?", or null.
  const [pending, setPending] = useState(null);

  const sorted = useMemo(
    () => sortLocationsForPicker(locations, { isOpen: openNowOf }),
    [locations]
  );
  const current = useMemo(
    () =>
      (Array.isArray(locations) ? locations : []).find(
        (location) => String(location?._id) === String(currentId)
      ) || null,
    [locations, currentId]
  );

  const choose = useCallback(
    (location) => {
      setOpen(false);
      if (!location || String(location._id) === String(currentId)) return;
      if (typeof confirmChoice === "function" && confirmChoice(location)) {
        setPending(location);
        return;
      }
      if (typeof onChoose === "function") onChoose(location);
    },
    [currentId, confirmChoice, onChoose]
  );

  const confirmPending = useCallback(() => {
    const next = pending;
    setPending(null);
    if (next && typeof onChoose === "function") onChoose(next);
  }, [pending, onChoose]);

  if (!Array.isArray(locations) || locations.length < 2) return null;

  const currentName = locationName(current, vendorName) || vendorName;
  const km = distanceText(current);
  // "1.2 km from Bulenga, Wakiso": a distance always says where from.
  const kmFrom = km && from ? `${km} from ${from}` : km;
  const pendingName = locationName(pending, vendorName);
  const moveCopy = moveCartCopy({ to: pendingName, from: currentName });

  return (
    <>
      <Bar
        type="button"
        $maxWidth={maxWidth}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`Ordering from ${currentName}${kmFrom ? `, ${kmFrom}` : ""}. Change branch`}
      >
        <LocationOnOutlinedIcon style={{ fontSize: 20, color: accent, flexShrink: 0 }} />
        <BarCopy>
          <BarLabel>Ordering from</BarLabel>
          <BarName>{currentName}</BarName>
          {kmFrom ? <BarFrom>{kmFrom}</BarFrom> : null}
        </BarCopy>
        <BarAction style={{ color: accent, borderColor: accent }}>
          <SwapHorizIcon style={{ fontSize: 16 }} aria-hidden="true" />
          Change branch
        </BarAction>
      </Bar>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="location-picker-title"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            // A bottom sheet on a phone, as in the apps.
            "@media (max-width: 600px)": {
              m: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "85vh",
              alignSelf: "flex-end",
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
        }}
      >
        <SheetHeader>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SheetTitle id="location-picker-title">Choose a branch</SheetTitle>
            <SheetSubtitle>{`${locations.length} branches · best for you first`}</SheetSubtitle>
          </div>
          <IconButton onClick={() => setOpen(false)} aria-label="Close" size="small">
            <CloseIcon style={{ color: MUTED }} />
          </IconButton>
        </SheetHeader>
        {/* The point every distance below is measured from, and the tap to
            change it. */}
        <FromRow>
          <NearMeOutlinedIcon style={{ fontSize: 18, color: MUTED, flexShrink: 0 }} aria-hidden="true" />
          <FromText>
            {from ? (
              <>
                Distances from <FromPlace>{from}</FromPlace>
              </>
            ) : (
              "Set your delivery location to see distances"
            )}
          </FromText>
          <FromChange
            type="button"
            style={{ color: accent }}
            onClick={() => {
              setOpen(false);
              setPromptOpen(true);
            }}
            aria-label={from ? "Change delivery location" : "Set your delivery location"}
          >
            {from ? "Change" : "Set"}
          </FromChange>
        </FromRow>
        <DialogContent sx={{ p: 0 }}>
          <RowList>
            {sorted.map((location) => (
              <LocationRow
                key={String(location._id)}
                location={location}
                vendorName={vendorName}
                selected={String(location._id) === String(currentId)}
                accent={accent}
                onChoose={choose}
              />
            ))}
          </RowList>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        aria-labelledby="location-switch-title"
        aria-describedby="location-switch-description"
      >
        <DialogTitle id="location-switch-title">{moveCopy.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="location-switch-description">
            {moveCopy.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} sx={{ color: MUTED }}>
            {moveCopy.stayLabel}
          </Button>
          <Button
            onClick={confirmPending}
            variant="contained"
            autoFocus
            sx={{
              background: accent,
              color: onAccent,
              "&:hover": { background: accent, color: onAccent, opacity: 0.92 },
            }}
          >
            {moveCopy.moveLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {promptOpen ? <LocationPrompt open onClose={() => setPromptOpen(false)} /> : null}
    </>
  );
};

export default LocationPicker;

const Bar = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: ${(p) => p.$maxWidth || "none"};
  margin: 0 auto 12px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid ${HAIRLINE};
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  font: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  }

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`;

const BarCopy = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const BarLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: ${MUTED};
`;

const BarName = styled.span`
  margin-top: 1px;
  font-size: 14px;
  font-weight: 700;
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarFrom = styled.span`
  margin-top: 1px;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarAction = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px 12px 18px;
  border-bottom: 1px solid ${HAIRLINE};
`;

const SheetTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: ${INK};
`;

const SheetSubtitle = styled.p`
  margin: 2px 0 0;
  font-size: 12.5px;
  color: ${MUTED};
`;

const FromRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #f8fafc;
  border-bottom: 1px solid ${HAIRLINE};
`;

const FromText = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 12.5px;
  line-height: 17px;
  color: ${MUTED};
`;

const FromPlace = styled.strong`
  font-weight: 700;
  color: ${INK};
`;

const FromChange = styled.button`
  appearance: none;
  flex-shrink: 0;
  padding: 4px 2px;
  background: none;
  border: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`;

const RowList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li + li {
    border-top: 1px solid ${HAIRLINE};
  }
`;

const Row = styled.button`
  appearance: none;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  background: ${(p) => (p.$selected ? "#F8FAFC" : "#FFFFFF")};
  text-align: left;
  cursor: pointer;
  font: inherit;

  &:hover {
    background: #f8fafc;
  }

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: -2px;
  }
`;

const RowIcon = styled.span`
  width: 28px;
  display: flex;
  justify-content: center;
  padding-top: 1px;
  flex-shrink: 0;
`;

const RowCopy = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const RowName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowAddress = styled.span`
  margin-top: 2px;
  font-size: 12.5px;
  line-height: 17px;
  color: ${MUTED};
`;

const RowTags = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;

  &:empty {
    display: none;
  }
`;

const Tag = styled.span`
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
`;

const RowKm = styled.span`
  flex-shrink: 0;
  padding-top: 2px;
  font-size: 13px;
  font-weight: 700;
  color: ${INK};
`;
